import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import classNames from "classnames";
import { action, computed, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { logEvent } from "../Amplitude";
import { atom } from "../atom";
import { uiStateMobx } from "../SharedIframeState";
import { ActionType } from "../Types";
import { ContextMenu, ContextMenuItem } from "../ui/ContextMenu";
import {
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
} from "../ui/Dialog";
import { FeatherIcon } from "../ui/FeatherIcon";
import {
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  HeaderMenuClassName,
} from "../ui/Menu";
import { Tooltip } from "../ui/Tooltip";
import { canvasDispatch } from "./CanvasDispatch";
import {
  canvasAtom,
  canvasLayoutIdAtom,
  isEditingLayoutAtom,
  layoutsMetadataComputed,
  urlLayoutIdAtom,
} from "./CanvasState";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import { computeElkLayout } from "./PaneLayout";
import { MAGIC_LAYOUT_ID, MAGIC_LAYOUT_NAME } from "./SandboxConstants";
import { getNewLayoutId } from "./SandboxUtils";

type LayoutMetadata = { id: string; name: string };

function LayoutSettingsButton({
  layoutMetadata,
  showSettings,
  setShowSettings,
  onCloseMenu,
}: {
  layoutMetadata: LayoutMetadata;
  showSettings: boolean;
  setShowSettings: React.Dispatch<React.SetStateAction<boolean>>;
  onCloseMenu: () => void;
}) {
  return (
    <PopoverPrimitive.Root open={showSettings} onOpenChange={setShowSettings}>
      <PopoverPrimitive.Trigger asChild={true}>
        <button
          className={classNames(
            "absolute flex right-1.5 top-1.5 p-0.5 rounded-sm hover:bg-neutral-200 group-hover:text-neutral-500",
            showSettings
              ? "bg-neutral-200 text-neutral-500"
              : "text-neutral-300"
          )}
        >
          <FeatherIcon icon="more-horizontal" size="md" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="bottom"
          sideOffset={4}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          asChild={true}
        >
          <ContextMenu className="min-w-36">
            <ContextMenuItem
              onClick={action(() => {
                if (urlLayoutIdAtom.value !== layoutMetadata.id) {
                  ParentComlinkAPI.switchLayoutId(layoutMetadata.id);
                }
                runInAction(() => {
                  isEditingLayoutAtom.value = true;
                });
                onCloseMenu();
              })}
            >
              edit layout
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => {
                const newLayoutName = window.prompt(
                  `Rename ${layoutMetadata.name} to`
                );
                if (newLayoutName !== null) {
                  canvasDispatch([
                    ActionType.SetLayoutName,
                    {
                      layoutId: layoutMetadata.id,
                      name: newLayoutName,
                    },
                  ]);
                  setShowSettings(false);
                }
              }}
            >
              rename layout
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => {
                if (
                  window.confirm(
                    `Are you sure you want to delete ${layoutMetadata.name}?`
                  )
                ) {
                  canvasDispatch([
                    ActionType.DeleteLayout,
                    { layoutId: layoutMetadata.id },
                  ]);
                  if (urlLayoutIdAtom.value === layoutMetadata.id) {
                    ParentComlinkAPI.switchLayoutId(undefined);
                  }
                }
              }}
              color="red"
            >
              delete layout
            </ContextMenuItem>
          </ContextMenu>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function LayoutMenuItem({
  layoutMetadata,
  selectedLayoutId,
  onClose,
}: {
  layoutMetadata: LayoutMetadata;
  selectedLayoutId: string | undefined;
  onClose: () => void;
}) {
  const [showSettings, setShowSettings] = useState(false);
  return (
    <div className="relative group">
      <DropdownMenuItem
        onClick={action(() => {
          if (layoutMetadata.id !== selectedLayoutId) {
            ParentComlinkAPI.switchLayoutId(layoutMetadata.id);
          }
          isEditingLayoutAtom.value = false;
          onClose();
        })}
        className={classNames("w-full group-hover:bg-neutral-100", {
          "bg-neutral-100": showSettings,
        })}
      >
        {layoutMetadata.name}
      </DropdownMenuItem>
      <LayoutSettingsButton
        layoutMetadata={layoutMetadata}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        onCloseMenu={onClose}
      />
    </div>
  );
}

function LayoutMenu({
  layoutsMetadata,
  selectedLayoutId,
  onClose,
}: {
  layoutsMetadata: LayoutMetadata[] | undefined;
  selectedLayoutId: string | undefined;
  onClose: () => void;
}) {
  return (
    <>
      <DropdownMenuItem
        onClick={() => {
          if (selectedLayoutId !== undefined) {
            ParentComlinkAPI.switchLayoutId(undefined);
          }
          onClose();
        }}
      >
        source
      </DropdownMenuItem>
      {/* {sandboxLayoutOverrideBox.get() !== undefined ? (
        <DropdownMenuItem
          onClick={action(() => {
            sandboxLayoutOverrideBox.set(undefined);
            onClose();
          })}
        >
          exit sandbox
        </DropdownMenuItem>
      ) : (
        <div className="relative group">
          <DropdownMenuItem
            onClick={action(() => {
              if (selectedLayoutId !== undefined) {
                ParentComlinkAPI.switchLayoutId(undefined);
              }
              sandboxLayoutOverrideBox.set({});
              onClose();
            })}
            className="w-full group-hover:bg-neutral-100 active:bg-neutral-150"
          >
            sandbox layout
          </DropdownMenuItem>
          <Tooltip
            side="bottom"
            content={
              <div className="w-48 whitespace-normal">
                Pane layout changes are not saved. The layout is reverted when
                you exit the sandbox.{" "}
                <span className="text-neutral-200">⌃⌘L</span>
              </div>
            }
          >
            <div className="absolute p-1 top-1 right-1 text-neutral-300 hover:text-neutral-600 transition-colors">
              <FeatherIcon icon="help-circle" size="md" />
            </div>
          </Tooltip>
        </div>
      )} */}
      {layoutsMetadata !== undefined
        ? layoutsMetadata.map((layoutMetadata) => (
            <LayoutMenuItem
              layoutMetadata={layoutMetadata}
              selectedLayoutId={selectedLayoutId}
              onClose={onClose}
              key={layoutMetadata.id}
            />
          ))
        : null}
      <div className="relative group">
        <DropdownMenuItem
          onClick={() => {
            if (selectedLayoutId !== MAGIC_LAYOUT_ID) {
              ParentComlinkAPI.switchLayoutId(MAGIC_LAYOUT_ID);
            }
            onClose();
            logEvent("select magic layout");
          }}
          className="w-full group-hover:bg-neutral-100 active:bg-neutral-150"
        >
          {MAGIC_LAYOUT_NAME}
        </DropdownMenuItem>
        <Tooltip
          content={
            <div>
              <div>very experimental!</div>
              <div>dynamic layout</div>
            </div>
          }
        >
          <div className="absolute p-1 top-1 right-1 text-neutral-300 hover:text-neutral-600 transition-colors">
            <FeatherIcon icon="help-circle" size="md" />
          </div>
        </Tooltip>
      </div>
      <div className="h-px w-full my-1.5 bg-neutral-100" />
      <DropdownMenuItem
        onClick={() => {
          const layoutId = getNewLayoutId();
          canvasDispatch([
            ActionType.CreateLayout,
            {
              layoutId,
              name: `layout ${
                LAYOUT_NAME_EMOJI[
                  Math.floor(Math.random() * LAYOUT_NAME_EMOJI.length)
                ]
              }`,
            },
          ]);
          ParentComlinkAPI.switchLayoutId(layoutId);
          runInAction(() => {
            isEditingLayoutAtom.value = true;
          });
          onClose();
        }}
      >
        <div className="flex items-center gap-2">
          <FeatherIcon icon="plus" size="md" className="opacity-50" />
          add layout
        </div>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={action(() => {
          showDefaultLayoutDialogAtom.value = true;
        })}
      >
        <div className="flex items-center gap-2">
          <FeatherIcon icon="home" size="md" className="opacity-50" />
          set default
        </div>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          if (
            window.confirm("You cannot undo autolayout (for now). Continue?")
          ) {
            async function go() {
              const layoutId = canvasLayoutIdAtom.value;
              if (layoutId === MAGIC_LAYOUT_ID) {
                return;
              }
              const elkPaneRects = await computeElkLayout(
                canvasAtom.value.panes,
                undefined
              );
              if (canvasLayoutIdAtom.value !== layoutId) {
                return;
              }
              if (layoutId === undefined) {
                canvasDispatch([
                  ActionType.SetPaneRectsMulti,
                  { paneRects: elkPaneRects },
                ]);
              } else {
                const layout = layoutsMetadata?.find(
                  (layout) => layout.id === layoutId
                );
                if (layout === undefined) {
                  return;
                }
                canvasDispatch([
                  ActionType.SetLayoutPaneRectsMulti,
                  {
                    layoutId,
                    paneRects: elkPaneRects,
                  },
                ]);
              }
            }
            go();
            logEvent("apply elk layout");
            onClose();
          }
        }}
      >
        <div className="flex items-center gap-2">
          <FeatherIcon icon="grid" size="md" className="opacity-50" />
          autolayout
        </div>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => {
          window.open(
            "https://www.notion.so/paulshen/Documentation-44fea11f393a4e46b5a22f5799de58ca#5cc4181b22324830a462a876e06e1b78"
          );
        }}
      >
        <div className="flex items-center gap-2">
          <FeatherIcon icon="help-circle" size="md" className="opacity-50" />
          layout help
        </div>
      </DropdownMenuItem>
    </>
  );
}

function DefaultLayoutButton({
  layoutId,
  layoutName,
  selected,
}: {
  layoutId: string | undefined;
  layoutName: string;
  selected: boolean;
}) {
  return (
    <button
      onClick={() => {
        canvasDispatch([ActionType.SetDefaultLayout, { layoutId }]);
      }}
      className={classNames(
        "w-full text-left font-medium rounded px-4 py-3 transition",
        selected ? "dialog-button-selected" : "dialog-button"
      )}
    >
      {layoutName}
    </button>
  );
}
const DefaultLayoutDialogContent = observer(() => {
  const defaultLayoutId = computed(
    () => canvasAtom.value.defaultLayoutId
  ).get();
  const layoutsMetadata = layoutsMetadataComputed.get();
  return (
    <div>
      <DialogHeader>default layout</DialogHeader>
      <div className="pt-4 pb-6 px-6">
        <div className="mb-4 text-neutral-500">
          The default layout is shown when someone visits the canvas without an
          explicit layout.
        </div>
        <div className="flex flex-col gap-2">
          <DefaultLayoutButton
            layoutId={undefined}
            layoutName="source"
            selected={defaultLayoutId === undefined}
          />
          {layoutsMetadata.map((layout) => (
            <DefaultLayoutButton
              layoutId={layout.id}
              layoutName={layout.name}
              selected={layout.id === defaultLayoutId}
              key={layout.id}
            />
          ))}
          {layoutsMetadata.length === 0 ? (
            <div className="text-neutral-400 mt-2">
              This canvas has only one layout. Create layouts to make alternate
              views of the canvas.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

const showDefaultLayoutDialogAtom = atom(false);
const DefaultLayoutDialogController = observer(() => {
  return (
    <DialogRoot
      open={showDefaultLayoutDialogAtom.value}
      onOpenChange={action((open) => {
        if (!open) {
          showDefaultLayoutDialogAtom.value = false;
        }
      })}
    >
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="w-[90vw] max-w-xs">
          <DefaultLayoutDialogContent />
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
});

const LAYOUT_NAME_EMOJI = [
  "🥢",
  "🍵",
  "🍡",
  "🍥",
  "🍘",
  "🍚",
  "🍙",
  "🍱",
  "🍣",
  "🍛",
  "🍲",
  "🍜",
  "🍢",
  "🥡",
];
export const HeaderLayoutButton = observer(() => {
  const [open, setOpen] = useState(false);
  const layoutId = canvasLayoutIdAtom.value;
  const layoutsMetadata = layoutsMetadataComputed.get();
  const hasMaximizedPane = computed(
    () => uiStateMobx.maximizedPaneId !== undefined
  ).get();

  return (
    <>
      <DropdownMenuRoot open={open} onOpenChange={setOpen} modal={false}>
        <DropdownMenuTrigger asChild={true}>
          <button
            className={classNames("h-full group relative", {
              hidden: hasMaximizedPane,
            })}
          >
            <div
              className={classNames(
                "h-full pl-2 pr-5 text-neutral-600 flex items-center pointer-events-auto",
                open ? "bg-secondary-100" : "group-hover:bg-neutral-50"
              )}
            >
              <FeatherIcon icon="layout" size="md" className="mr-2" />
              {layoutId === undefined
                ? "source"
                : layoutId === MAGIC_LAYOUT_ID
                ? MAGIC_LAYOUT_NAME
                : layoutsMetadata?.find((layout) => layout.id === layoutId)
                    ?.name ?? ""}
            </div>
            <div
              className={classNames(
                "flex items-center absolute pl-0.5 pr-1 right-0 top-0 bottom-0 group2",
                {
                  "focus:outline-none": open,
                }
              )}
            >
              <FeatherIcon
                icon="chevron-down"
                size="sm"
                className={classNames(
                  "transform transition-transform",
                  open ? "translate-y-0.5" : "group2-hover:translate-y-0.5"
                )}
              />
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuPrimitive.Portal>
          <DropdownMenuPrimitive.Content
            align="end"
            className={HeaderMenuClassName}
          >
            <LayoutMenu
              layoutsMetadata={layoutsMetadata}
              selectedLayoutId={layoutId}
              onClose={() => setOpen(false)}
            />
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuRoot>
      <DefaultLayoutDialogController />
    </>
  );
});
